import cookie from './cookie';
import { requestErrorText, showRequestError } from './request-feedback';
import axios from 'axios';
import { getBaseApi } from './base_url';
import { firebaseAuthApp } from '../firebase/auth';
import { getAuth } from 'firebase/auth';
import { isEmailNotVerified, signOutAdmin, verifiedAdminToken } from '../firebase/admin_session';

const BASE_URL = `https://${getBaseApi()}/api/admin`;
let authorizationRefresh;

export const reportAnonymousLoginFailure = data => axios.post(`${BASE_URL}/pub/audit/login-failure`, data, { timeout: 10000 });

function refreshAuthorization() {
    if (!authorizationRefresh) {
        authorizationRefresh = (async () => {
            const auth = getAuth(firebaseAuthApp());
            if (!auth) throw new Error('No authentication session');
            await auth.authStateReady();
            if (!auth.currentUser) throw new Error('No authentication session');
            const user = auth.currentUser;
            const token = await verifiedAdminToken(user);
            if (auth.currentUser !== user) throw new Error('Authentication session changed');
            cookie.setCookie('user-token', token);
        })().finally(() => { authorizationRefresh = undefined; });
    }
    return authorizationRefresh;
}

const normalizeError = error => {
    // Keep status, response and identity for conflict handling; existing page
    // handlers also receive the same readable text as the shared notification.
    if (axios.isAxiosError(error)) error.message = requestErrorText(error);
    return error;
};

async function sendAuthorized(send) {
    try { return await send(); }
    catch (error) {
        if (isEmailNotVerified(error)) {
            await signOutAdmin(getAuth(firebaseAuthApp())).catch(() => {});
            throw normalizeError(error);
        }
        if (error.response?.status !== 401) throw normalizeError(error);
        try { await refreshAuthorization(); }
        catch (refreshError) {
            if (isEmailNotVerified(refreshError)) {
                await signOutAdmin(getAuth(firebaseAuthApp())).catch(() => {});
                throw refreshError;
            }
            throw normalizeError(error);
        }
        // A retry is attempted once. A rejected refresh or retry always settles
        // the caller, so finally handlers can release their loading indicators.
        try { return await send(); }
        catch (retryError) {
            if (isEmailNotVerified(retryError)) await signOutAdmin(getAuth(firebaseAuthApp())).catch(() => {});
            throw normalizeError(retryError);
        }
    }
}

const headers = (contentType = 'application/json') => ({
    'Content-Type': contentType,
    Authorization: `Bearer ${cookie.getCookie('user-token') || ''}`,
});

async function mutation(send) {
    try { return (await sendAuthorized(send)).data; }
    catch (error) { showRequestError(error); return false; }
}

async function post(url, params, appCheckToken = null) {
    const external = url.includes('googleapis');
    const send = () => axios.post(external ? url : BASE_URL + url, params, {
        timeout: 60000,
        headers: {
            ...(external ? { 'Content-Type': 'application/json' } : headers()),
            ...(appCheckToken ? { 'X-Firebase-Appcheck': appCheckToken } : {}),
        },
    });
    if (!external) return mutation(send);
    // Authentication providers have their own login form error handling.
    try { return (await send()).data; } catch { return false; }
}

async function get(url, params) {
    return (await sendAuthorized(() => axios.get(BASE_URL + url, {
        params, timeout: 60000, headers: headers('application/x-www-form-urlencoded'),
        ...(url.includes('download') ? { responseType: 'arraybuffer' } : {}),
    }))).data;
}

const patch = (url, params) => mutation(() => axios.patch(BASE_URL + url, params, { headers: headers(), timeout: 60000 }));
const dataDel = url => mutation(() => axios.delete(BASE_URL + url, { params: {}, headers: headers('application/x-www-form-urlencoded'), timeout: 60000 }));

export async function reAuthentication(callback) {
    try { await refreshAuthorization(); return await callback(); }
    catch (error) {
        if (isEmailNotVerified(error)) await signOutAdmin(getAuth(firebaseAuthApp())).catch(() => {});
        showRequestError(error, '登录状态已失效，请重新登录。'); return false;
    }
}
export const reAuthenticationForStorage = reAuthentication;

const request = { post, get, patch, dataDel };
export default request;

// Management editors retain rejected errors and their HTTP status for revision
// conflicts; their UI boundary is responsible for displaying the notification.
export async function authorizedRequest(method, path, { data, params } = {}) {
    return (await sendAuthorized(() => axios.request({
        method, url: BASE_URL + path, data, params, timeout: 60000, headers: headers(),
    }))).data;
}
